import { useEffect } from 'react';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

export const useSchemaOrg = (schemaData: SchemaOrgData | SchemaOrgData[]) => {
  useEffect(() => {
    const scriptId = 'schema-org-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(schemaData);
    
    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [schemaData]);
};

export const getOrganizationSchema = (): SchemaOrgData => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://www.geosys.be';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'GeoSys IT Solutions',
    'url': siteUrl,
    'logo': `${siteUrl}/vite.svg`,
    'description': 'Leading IT services provider in Kuurne, Kortrijk, and Roeselare delivering innovative IT infrastructure, cybersecurity, managed services, digitalisation, backup solutions, surveillance systems, audiovisual solutions, project management, and CISO-as-a-Service across West Flanders.',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Kuurne',
      'addressRegion': 'West Flanders',
      'addressCountry': 'BE',
      'postalCode': '8520'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '50.8514',
      'longitude': '3.2858'
    },
    'email': 'info@geosys.be',
    'areaServed': [
      {
        '@type': 'City',
        'name': 'Kuurne'
      },
      {
        '@type': 'City',
        'name': 'Kortrijk'
      },
      {
        '@type': 'City',
        'name': 'Roeselare'
      },
      {
        '@type': 'State',
        'name': 'West Flanders'
      }
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'Customer Service',
      'email': 'info@geosys.be',
      'availableLanguage': ['English', 'Dutch', 'French'],
      'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare', 'West Flanders']
    }
  };
};

export const getProfessionalServiceSchema = (): SchemaOrgData => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://www.geosys.be';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    'name': 'GeoSys IT Solutions',
    'url': siteUrl,
    'description': 'Professional IT services provider in Kuurne, Kortrijk, and Roeselare offering comprehensive IT infrastructure, cybersecurity, managed services, digitalisation, backup solutions, surveillance systems, audiovisual solutions, project management, and CISO-as-a-Service across West Flanders.',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Kuurne',
      'addressRegion': 'West Flanders',
      'addressCountry': 'BE',
      'postalCode': '8520'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '50.8514',
      'longitude': '3.2858'
    },
    'email': 'info@geosys.be',
    'areaServed': [
      {
        '@type': 'City',
        'name': 'Kuurne',
        'sameAs': 'https://en.wikipedia.org/wiki/Kuurne'
      },
      {
        '@type': 'City',
        'name': 'Kortrijk',
        'sameAs': 'https://en.wikipedia.org/wiki/Kortrijk'
      },
      {
        '@type': 'City',
        'name': 'Roeselare',
        'sameAs': 'https://en.wikipedia.org/wiki/Roeselare'
      }
    ],
    'priceRange': '$$',
    'serviceType': [
      'IT Infrastructure Kuurne',
      'IT Infrastructure Kortrijk',
      'IT Infrastructure Roeselare',
      'Cybersecurity Services Kuurne',
      'Cybersecurity Services Kortrijk',
      'Cybersecurity Services Roeselare',
      'Managed IT Services Kuurne',
      'Managed IT Services Kortrijk',
      'Managed IT Services Roeselare',
      'Digitalisation',
      'Backup Solutions',
      'Surveillance Systems',
      'Audiovisual Solutions',
      'Project Management',
      'CISO-as-a-Service'
    ],
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'IT Services in Kuurne, Kortrijk, and Roeselare',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'IT Infrastructure Services in Kuurne, Kortrijk, and Roeselare',
            'description': 'Robust and scalable IT infrastructure solutions tailored to businesses in Kuurne, Kortrijk, and Roeselare.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Cybersecurity Services in Kuurne, Kortrijk, and Roeselare',
            'description': 'Comprehensive cybersecurity solutions to protect businesses in Kuurne, Kortrijk, and Roeselare from digital threats.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Managed IT Services in Kuurne, Kortrijk, and Roeselare',
            'description': 'Proactive IT management and support for businesses in Kuurne, Kortrijk, and Roeselare.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Digitalisation Services',
            'description': 'Transform your business with cutting-edge digital solutions and automation.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Backup Solutions',
            'description': 'Secure and reliable backup systems to protect your critical data.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Surveillance Systems',
            'description': 'Advanced surveillance and monitoring solutions for enhanced security in Kuurne, Kortrijk, and Roeselare.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Audiovisual Solutions',
            'description': 'Professional audiovisual installations and solutions for businesses in West Flanders.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'Project Management',
            'description': 'Expert project management for successful IT implementations.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'CISO-as-a-Service',
            'description': 'Virtual Chief Information Security Officer services for strategic security leadership.',
            'areaServed': ['Kuurne', 'Kortrijk', 'Roeselare']
          }
        }
      ]
    }
  };
};

export const getWebPageSchema = (): SchemaOrgData => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://www.geosys.be';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'IT Services Kuurne Kortrijk Roeselare | GeoSys IT Solutions West Flanders',
    'url': siteUrl,
    'description': 'Leading IT services provider in Kuurne, Kortrijk, and Roeselare offering IT infrastructure, cybersecurity, managed services, digitalisation, backup solutions, surveillance systems, audiovisual solutions, project management, and CISO-as-a-Service across West Flanders.',
    'inLanguage': ['en', 'nl', 'fr'],
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'GeoSys IT Solutions',
      'url': siteUrl
    },
    'about': {
      '@type': 'Thing',
      'name': 'IT Services in Kuurne, Kortrijk, and Roeselare',
      'description': 'Professional IT services and solutions for businesses in West Flanders'
    },
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Home',
          'item': siteUrl
        }
      ]
    },
    'spatialCoverage': [
      {
        '@type': 'Place',
        'name': 'Kuurne',
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': '50.8514',
          'longitude': '3.2858'
        }
      },
      {
        '@type': 'Place',
        'name': 'Kortrijk',
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': '50.8279',
          'longitude': '3.2647'
        }
      },
      {
        '@type': 'Place',
        'name': 'Roeselare',
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': '50.9467',
          'longitude': '3.1236'
        }
      }
    ]
  };
};

export const getLocalBusinessSchema = (): SchemaOrgData => {
  const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://www.geosys.be';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'GeoSys IT Solutions',
    'image': `${siteUrl}/vite.svg`,
    'url': siteUrl,
    'telephone': '',
    'email': 'info@geosys.be',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Kuurne',
      'addressRegion': 'West Flanders',
      'addressCountry': 'BE',
      'postalCode': '8520'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '50.8514',
      'longitude': '3.2858'
    },
    'openingHoursSpecification': [
      {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        'opens': '09:00',
        'closes': '17:00'
      }
    ],
    'priceRange': '$$',
    'areaServed': [
      {
        '@type': 'City',
        'name': 'Kuurne'
      },
      {
        '@type': 'City',
        'name': 'Kortrijk'
      },
      {
        '@type': 'City',
        'name': 'Roeselare'
      }
    ],
    'hasMap': 'https://www.google.com/maps/place/Kuurne,+Belgium/@50.8514,3.2858,13z'
  };
};
